import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { ticketAffectedItemsQuery, type TicketAffectedItem } from '../api/easeedesk'
import { affectedItemChoicesQuery, attachAffectedItem, detachAffectedItem, type AffectedItemReply } from '../api/easeedeskTicketTabs'
import { newAlert } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { Spinner } from '../ui/Spinner'
import './TicketAffectedItems.css'
import { NEW_TAB } from '../ui/NewTab'

/**
 * Fila "Affected items" - `easeedesk/tickets/details/affected_items.php`.
 *
 * Un singur tabel din doua tabele: elementele de configurare scrise pe tichet si
 * echipamentele atasate pe calea veche. Pentru cine citeste tichetul e o singura
 * intrebare, iar despartite ar arata numai care tabela s-a nimerit sa fie
 * folosita.
 *
 * Adaugarea si scoaterea sunt cele din macheta veche: lista de alegere
 * (`selectpicker` cu cautare, adusa O DATA la deschiderea filei), butonul
 * "Add", si cate un "x" pe randurile pe care le detine tabela de elemente.
 * Dupa fiecare scriere lista se cere din nou de la server, nu se carpeste
 * aici - doi desenatori ai aceleiasi liste se despart. Mesajul se scrie in
 * cutia de sub tabel, DUPA reincarcare, ca in scriptul vechi: scris inainte,
 * era sters de reincarcare in aceeasi clipa.
 */

export function TicketAffectedItems({ id }: { id: number }) {
  const { data, error } = useQuery(ticketAffectedItemsQuery(id))
  const client = useQueryClient()
  const alerts = useRef<HTMLDivElement>(null)
  const [busy, setBusy] = useState(false)

  /*
   * `newAlert(...)` in `#affectedItemsAlert-<id>`, cu iconita dupa fel, ca
   * `say()` din macheta: 4 secunde.
   */
  const say = (message: string, type: 'success' | 'danger' | 'info') => {
    newAlert(`<i class="fas fa-${type === 'success' ? 'check' : 'exclamation'}-circle"></i> ${message}`, type, 4000, alerts.current)
  }

  const reload = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id, 'affected-items'] }),
      /* Numarul de elemente afectate e si pe randul de sus al fisei. */
      client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id], exact: true }),
    ])

  const write = (call: Promise<AffectedItemReply>, failed: string) => {
    setBusy(true)
    call
      .then((reply) => reload().then(() => say(reply.message, reply.status === 'OK' ? 'success' : 'danger')))
      .catch(() => say(failed, 'danger'))
      .finally(() => setBusy(false))
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const window = data.window
  /* Cu banda ferestrei deasupra se tace: "nimic afectat" scris sub "cuprinde
     340 de elemente" e ecranul contrazicandu-se singur. */
  const wideWindow = window !== null && window.scopeType !== 3

  return (
    <div id={`aiTab${id}`}>
      {wideWindow && (
        <div className={`note note-${window.itemCount === 0 ? 'danger' : 'warning'} ai-window`}>
          <i className="fas fa-calendar-alt"></i> <strong>Covers {window.scopeWord}:</strong> {window.scopeName} &mdash;{' '}
          {window.itemCount === 0 ? (
            <>
              <strong>nothing is in scope right now</strong>, so this window authorises no change. Check that the class is the one you meant and that the customer&rsquo;s items are in the
              CMDB.
            </>
          ) : (
            <>
              {window.itemCount.toLocaleString()} item{window.itemCount === 1 ? '' : 's'} in scope right now.
              <br />
              <span className="ai-muted">They are not listed one by one below: the window is the record, and it stays right as items join or leave the class.</span>
            </>
          )}
        </div>
      )}

      {data.closed && (
        <div className="note note-info ai-window">
          <i className="fas fa-lock"></i> This ticket is resolved, so what it affected is now a record and cannot be changed.
        </div>
      )}

      {!data.closed && data.editable && (
        <Picker
          id={id}
          busy={busy}
          onAdd={(ci) => write(attachAffectedItem(id, ci), 'The item could not be added.')}
          onEmpty={() => say('This customer has no other items to add.', 'info')}
        />
      )}

      {data.items.length === 0 ? (
        !wideWindow && (
          <div className="ai-empty">
            <i className="fas fa-server"></i>
            Nothing is recorded as affected by this ticket yet.
          </div>
        )
      ) : (
        <table className="table table-hover ai-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Class</th>
              <th>Status</th>
              <th>Health</th>
              <th>Impact</th>
              <th>Recorded</th>
              {data.editable && <th style={{ width: 40 }}></th>}
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <Row
                key={item.ciId !== null ? `ci-${item.ciId}` : `dev-${item.deviceId}`}
                item={item}
                editable={data.editable}
                busy={busy}
                onRemove={(ci) => write(detachAffectedItem(id, ci), 'The item could not be removed.')}
              />
            ))}
          </tbody>
        </table>
      )}

      <div id={`affectedItemsAlert-${id}`} ref={alerts}></div>
    </div>
  )
}

/**
 * Lista de alegere si butonul "Add" - `.affectedItemsPicker` + `.affectedItemsAdd`.
 *
 * Eticheta fiecarei optiuni e cea din scriptul vechi: numele, clasa dupa o
 * linie, serialul in paranteze - asa cautarea gaseste si dupa serial, pentru
 * cine citeste eticheta de pe spatele masinii.
 */
function Picker({ id, busy, onAdd, onEmpty }: { id: number; busy: boolean; onAdd(ci: number): void; onEmpty(): void }) {
  const { data } = useQuery(affectedItemChoicesQuery(id))
  const [chosen, setChosen] = useState('')
  const toldEmpty = useRef(false)

  if (data && data.items.length === 0 && !toldEmpty.current) {
    toldEmpty.current = true
    /* Dupa desenare, nu in timpul ei: cutia de mesaje e in acelasi arbore. */
    setTimeout(onEmpty, 0)
  }

  const options = (data?.items ?? []).map((item) => ({
    value: String(item.id),
    label: item.name + (item.class ? ` — ${item.class}` : '') + (item.serial ? ` (${item.serial})` : ''),
  }))

  return (
    <div className="row" style={{ marginBottom: 15 }}>
      <div className="col-md-9">
        <SelectPicker
          id={`affectedItemsPicker-${id}`}
          className="form-control affectedItemsPicker"
          title="Search for an item to add…"
          size={10}
          options={options}
          value={chosen}
          onChange={setChosen}
        />
      </div>
      <div className="col-md-3">
        <button
          type="button"
          className="btn btn-info btn-block affectedItemsAdd"
          disabled={busy || chosen === ''}
          onClick={() => {
            if (!chosen) return
            onAdd(Number(chosen))
            setChosen('')
          }}
        >
          <i className="fas fa-plus"></i> Add
        </button>
      </div>
    </div>
  )
}

function Row({ item, editable, busy, onRemove }: { item: TicketAffectedItem; editable: boolean; busy: boolean; onRemove(ci: number): void }) {
  return (
    <tr data-ci={item.ciId ?? 0}>
      <td>
        {item.ciId !== null ? (
          <a href={`/app/cmdb/view/${item.ciId}`} {...NEW_TAB}>
            {item.name}
          </a>
        ) : (
          item.name
        )}{' '}
        {item.isDevice &&
          (item.deviceId !== null ? (
            /* Eticheta duce la echipamentul din Inventoree: fisa CI spune ce rol
               are in infrastructura, fisa echipamentului spune ce e in el. Cine
               repara are nevoie de a doua. */
            <a className="label label-default ai-label" href={`/app/inventoree/device/${item.deviceId}`} title="Open the equipment in Inventoree" {...NEW_TAB}>
              <i className="fas fa-desktop"></i> device
            </a>
          ) : (
            <span className="label label-default ai-label">
              <i className="fas fa-desktop"></i> device
            </span>
          ))}
        {item.outsideCmdb && (
          <span className="label label-warning ai-label" title="This equipment is not in the CMDB, so there is nothing to open">
            not in CMDB
          </span>
        )}
      </td>
      <td className="text-muted">{item.className || '—'}</td>
      <td className="text-muted">
        {item.status || '—'}
        {item.criticality !== '' && (
          <>
            <br />
            <small>{item.criticality}</small>
          </>
        )}
      </td>
      <td>
        {item.health !== '' ? <span className={`label label-${item.healthTone} ai-label`}>{item.health}</span> : <span className="text-muted">&mdash;</span>}
      </td>
      <td className="text-muted">
        {/* Zero nu se scrie: "0 affected" se citeste ca o masuratoare lipsa. */}
        {item.impactCount > 0 && item.ciId !== null ? (
          <a href={`/app/adm/impact/${item.ciId}`} {...NEW_TAB} title="What breaks with it">
            {item.impactCount} affected
          </a>
        ) : (
          <>&mdash;</>
        )}
      </td>
      <td className="text-muted ai-when">
        {item.origin}
        {item.recordedAt !== '' && (
          <>
            <br />
            {item.recordedAt}
          </>
        )}
      </td>
      {editable && (
        <td className="text-right">
          {/* Numai ce detine tabela de elemente se scoate de aici; un aparat
              atasat pe calea veche se scoate cu controlul lui, si un buton care
              ar tacea ar fi mai rau decat lipsa lui. */}
          {item.ciId !== null && item.removable && (
            <button
              type="button"
              className="btn btn-link text-danger affectedItemsRemove"
              data-ci={item.ciId}
              data-name={item.name}
              title="Remove from this ticket"
              disabled={busy}
              onClick={() => onRemove(item.ciId as number)}
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </td>
      )}
    </tr>
  )
}
